<template>
  <div>
    <ErrorDisplay :errors="errors"></ErrorDisplay>

    <div v-if="isLoginStep" class="form-group form-unit">
      <label class="form-unit__label form-unit__label--upper-sm form-unit__label--w-link"
        >{{ $t("template.phone_number") }}
      </label>
      <VueTelInput
        name="phone"
        v-model="phone"
        v-validate="{
          required: true,
          twilio_lookup: twilioLookup,
        }"
        :inputOptions="{
          styleClasses: 'form-control form-unit__input',
        }"
        mode="international"
        data-vv-validate-on="blur"
        valid-characters-only
        tabindex="4"
        @validate="validate"
        :defaultCountry="defaultCountry"
        :autoDefaultCountry="autoDefaultCountry"
      />
      <span
        v-if="vErrorsHas('phone', 'required') || vErrorsHas('phone', 'decimal')"
        class="v-help is-danger"
      >
        {{ $t("template.field_is_required") }}
      </span>
      <span v-if="twilioLookupErrorMsg" class="v-help is-danger">
        {{ twilioLookupErrorMsg }}
      </span>
    </div>

    <!-- Step 2 - enter phone code 6 digits -->
    <div v-else>
      <div class="form-group form-unit">
        <label class="form-unit__label form-unit__label--upper-sm form-unit__label--w-link">
          {{ $t("template.phone_code", "Phone Code") }}
        </label>
        <div class="tw-flex tw-gap-2 tw-justify-center tw-mt-2">
          <input
            v-for="(digit, index) in phoneCodeDigits"
            :key="index"
            :ref="`phoneCodeInput${index}`"
            type="number"
            v-model="phoneCodeDigits[index]"
            class="hide-arrows form-control tw-w-12 tw-h-12 tw-text-center tw-text-lg tw-font-semibold"
            maxlength="1"
            :tabindex="5 + index"
            @input="onDigitInput(index, $event)"
            @keydown="onDigitKeydown(index, $event)"
            @paste="onPaste($event)"
          />
        </div>
        <span v-if="phoneCodeError" class="v-help is-danger tw-block tw-text-center tw-mt-2">
          {{ phoneCodeError }}
        </span>
      </div>
    </div>

    <!-- actions -->
    <div class="tw-flex tw-justify-center tw-mt-4">
      <div
        v-if="!isPhoneCodeStep"
        type="button"
        class="bttn bttn--lg bttn--orange"
        :disabled="loading"
        @click="loginUser"
      >
        {{ $t("template.sign_in_btn", "Sign In") }}
      </div>

      <div
        v-if="isPhoneCodeStep"
        class="tw-mt-[36px] bttn bttn--lg bttn--blue"
        :disabled="loading"
        @click.prevent="activeStep = 'login'"
      >
        {{ $t("template.log_in_back", "Back") }}
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from "vue";
import { mapState } from "vuex";
import { VueTelInput } from "vue-tel-input";
import ErrorDisplay from "@/views/Error.vue";
import axios from "axios";
import urls from "@/constants/urls";
import router from "@/router";

export default defineComponent({
  name: "PhoneLoginForm",
  components: {
    VueTelInput,
    ErrorDisplay,
  },
  data() {
    return {
      login: {
        phone: "",
        phone_code: "",
        phone_code_token: "",
      },
      loading: false,
      phoneObject: null,
      activeStep: "login", // 'login' or 'enterPhoneCode'
      phoneCodeDigits: ["", "", "", "", "", ""],
      phoneCodeError: "",
    };
  },
  computed: {
    ...mapState({
      errors: (state) => state.user.errorLogin,
      defaultCountry: (s) => s.account.userCountry,
    }),
    phone: {
      get() {
        return this.login.phone || "";
      },
      set(val) {
        this.$set(this.login, "phone", val);
      },
    },
    isLoginStep() {
      return this.activeStep === "login";
    },
    isPhoneCodeStep() {
      return this.activeStep === "enterPhoneCode";
    },
    twilioLookupErrorMsg() {
      const exist = this.vErrors.items.find(
        (el) => el.field === "phone" && el.rule === "twilio_lookup"
      );

      if (exist) {
        return this.$t("template.field_error_twilio_lookup", "Invalid or incorrect phone number");
      }

      return "";
    },
    twilioLookup() {
      return {
        valid: this.phoneObject?.valid || false,
      };
    },
    autoDefaultCountry() {
      return this.defaultCountry === "N/A" || this.defaultCountry.length === 0;
    },
  },
  methods: {
    validate(phoneObject) {
      this.phoneObject = phoneObject;

      if (!this.phoneObject?.valid) {
        return;
      }
    },
    vErrorsHas(field, rule) {
      return this.vErrors.items.find((el) => el.field === field && el.rule === rule);
    },
    onDigitInput(index, event) {
      const value = event.target.value;

      // Only allow digits
      if (!/^\d*$/.test(value)) {
        this.$set(this.phoneCodeDigits, index, "");
        return;
      }

      // Update the digit
      this.$set(this.phoneCodeDigits, index, value);

      // Clear any previous errors
      this.phoneCodeError = "";

      // Auto-advance to next field if digit entered and not the last field
      if (value && index < 5) {
        this.focusNextInput(index + 1);
      }

      // Auto-submit if all 6 digits are filled
      if (
        this.phoneCodeDigits.every((digit) => digit !== "") &&
        this.phoneCodeDigits.join("").length === 6
      ) {
        this.autoSubmit();
      }
    },
    onDigitKeydown(index, event) {
      // Handle backspace to move to previous field
      if (event.key === "Backspace" && !this.phoneCodeDigits[index] && index > 0) {
        this.focusPreviousInput(index - 1);
      }

      // Handle arrow keys for navigation
      if (event.key === "ArrowLeft" && index > 0) {
        event.preventDefault();
        this.focusPreviousInput(index - 1);
      } else if (event.key === "ArrowRight" && index < 5) {
        event.preventDefault();
        this.focusNextInput(index + 1);
      }
    },
    onPaste(event) {
      event.preventDefault();
      const pastedData = event.clipboardData.getData("text");
      const digits = pastedData.replace(/\D/g, "").slice(0, 6);

      for (let i = 0; i < 6; i++) {
        this.$set(this.phoneCodeDigits, i, digits[i] || "");
      }

      // Focus the next empty field or the last field if all are filled
      const nextEmptyIndex = this.phoneCodeDigits.findIndex((digit) => !digit);
      if (nextEmptyIndex !== -1) {
        this.focusNextInput(nextEmptyIndex);
      } else {
        this.focusNextInput(5);
        // Auto-submit if all fields are filled
        if (digits.length === 6) {
          this.autoSubmit();
        }
      }
    },
    focusNextInput(index) {
      this.$nextTick(() => {
        const inputs = this.$refs[`phoneCodeInput${index}`];
        if (inputs && inputs.length > 0) {
          inputs[0].focus();
        } else if (inputs) {
          inputs.focus();
        }
      });
    },
    focusPreviousInput(index) {
      this.$nextTick(() => {
        const inputs = this.$refs[`phoneCodeInput${index}`];
        if (inputs && inputs.length > 0) {
          inputs[0].focus();
        } else if (inputs) {
          inputs.focus();
        }
      });
    },
    autoSubmit() {
      // Add a small delay to ensure all digits are properly set
      this.$nextTick(() => {
        if (this.phoneCodeDigits.join("").length === 6) {
          this.loginUser();
        }
      });
    },
    loginUser() {
      if (this.loading) {
        return;
      }

      // Validate phone code if we're in the phone code step
      if (this.isPhoneCodeStep) {
        const phoneCode = this.phoneCodeDigits.join("");
        if (!phoneCode || phoneCode.length !== 6) {
          this.phoneCodeError = this.$t(
            "template.field_must_be_6_digits",
            "Field must be 6 digits"
          );
          return;
        }
        // Ensure the phoneCode is set in the login object
        this.$set(this.login, "phone_code", phoneCode);
      }

      if (!this.phoneObject?.valid && !this.isPhoneCodeStep && this.vErrors.items.length === 0) {
        this.$set(this.vErrors, "items", [
          ...this.vErrors.items,
          { field: "phone", rule: "twilio_lookup" },
        ]);
        return;
      }

      this.loading = true;
      return this.$store
        .dispatch("loginUser", [this.login, { noRouting: true }])
        .then((data) => {
          if (data?.user && this.isPhoneCodeStep) {
            if (router.currentRoute.query.redirect) {
              router.push({ path: router.currentRoute.query.redirect });
            } else {
              this.defineRouteAfterLogin(data?.user);
            }
          }
          if (!this.isPhoneCodeStep) {
            this.activeStep = "enterPhoneCode";
            this.phoneCodeDigits = ["", "", "", "", "", ""];
            this.$set(this.login, "phone_code", "");
            // Store the phone code token for later use
            this.login.phone_code_token = data?.user?.attributes?.phone_code_token;
          }
          return data;
        })
        .catch((error) => {
          // Handle phone code error
          if (this.isPhoneCodeStep && error?.response?.status === 400) {
            this.phoneCodeError = this.$t("template.invalid_phone_code", "Invalid phone code");
            // Clear the digits for retry
            this.phoneCodeDigits = ["", "", "", "", "", ""];
            this.$set(this.login, "phone_code", "");
            // Focus first input
            this.focusNextInput(0);
          }
        })
        .finally(() => {
          this.focusNextInput(0);
          this.loading = false;
        });
    },
    navigateTo(path) {
      if (router.currentRoute.path !== path) {
        localStorage.setItem("navChoice", path);
        router.push({ path });
      }
    },
    emitSelectDashboard() {
      this.$emit("selectDashboard");
    },
    async defineRouteAfterLogin(user) {
      const { donorDashboard, orgDashboard } = await this.checkDashboardsAvailability(user);
      const navChoice = localStorage.getItem("navChoice");

      const dashboardRoutes = {
        both: () => this.emitSelectDashboard(),
        donorOnly: () => this.navigateTo("/donor-dashboard"),
        orgOnly: () => this.navigateTo("/dashboard"),
        none: () => this.emitSelectDashboard(),
      };

      const getDashboardType = (donor, org) => {
        if (donor && org) return "both";
        if (donor && !org) return "donorOnly";
        if (!donor && org) return "orgOnly";
        return "none";
      };

      if (navChoice && router.currentRoute.path !== navChoice) {
        router.push({ path: navChoice });
      } else {
        const dashboardType = getDashboardType(donorDashboard, orgDashboard);
        dashboardRoutes[dashboardType]();
      }
    },
    async checkDashboardsAvailability() {
      try {
        const [donorDashboard, orgDashboard] = await Promise.all([
          axios
            .get(urls.donorAccount)
            .then((response) => {
              if (response?.data) {
                return response.data;
              }
              return false;
            })
            .catch(() => false),

          axios
            .get(urls.getOrganizations)
            .then((response) => {
              if (response?.data?.data) {
                return response.data.data;
              }
              return false;
            })
            .catch(() => false),
        ]);

        return {
          donorDashboard,
          orgDashboard,
        };
      } catch (e) {
        console.log(e);
        return {
          donorDashboard: false,
          orgDashboard: false,
        };
      }
    },
  },
});
</script>
